import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAppSelector } from "@application/store/useStore";
import { formatLocalizedDate } from "@infrastructure/helpers";
import { alpha, Box, Chip, Divider, Typography, useMediaQuery, useTheme, } from "@mui/material";
import { EText } from "@ui/components/event/typography/EText";
import { HeaderLinks } from "@ui/layout/header/EventHeader";
import { useEffect, useMemo, useRef, useState, } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import i18n from "../../../../i18n";
import DisplayFormattedMessage from "@ui/components/DisplayFormattedMessage";
const MemberBorder = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "start",
    borderLeft: "2px solid #00B6ED",
    paddingLeft: "22px",
    marginTop: "5px",
};
export const ExternalFormSummary = ({ activeStep, watchedValues, eventType, isDrawer = false, }) => {
    const theme = useTheme();
    const personInfo = watchedValues?.PersonalInfo || {};
    const { pickupLocations } = useAppSelector((state) => state.pickupLocation);
    const adultInfo = watchedValues?.AdultInfo || [];
    const childrenInfo = watchedValues?.ChildrenInfo || [];
    const importantInfo = watchedValues?.ImportantInfo || {};
    const accommodation = watchedValues?.Accommodation || {};
    const transport = watchedValues?.Transport || {};
    const { externalUsers } = useAppSelector((state) => state.external);
    const { t } = useTranslation();
    const { singleEvent } = useAppSelector((state) => state.event);
    const { linkVerification } = useAppSelector((state) => state.event);
    const isSmallScreen = useMediaQuery("(max-width:900px)");
    const isBelow1024 = useMediaQuery(theme.breakpoints.down(1025)); // to trigger responsiveness on 1024px
    const isBelow390 = useMediaQuery(theme.breakpoints.down(391)); // to trigger responsiveness on 391px
    const isTablet = useMediaQuery("(min-width:1024px)");
    const isOnly1024 = useMediaQuery(theme.breakpoints.between(1024, 1025));
    const { externalCountData } = useAppSelector((state) => state.external);
    const [countOppupiedSpaces, setCountOccupiedSpaces] = useState(0);
    const totalAttendees = Number(personInfo.AdultsCount || 0) +
        Number(personInfo.ChildrenCount || 0) +
        1;
    const hasDietary = importantInfo.DietaryRequirements &&
        importantInfo.DietaryRequirements.length > 0;
    const hasComments = importantInfo.Comments;
    const hasTransport = transport.TransportOption;
    const location = useLocation();
    const { invitationDates } = useAppSelector((state) => state.invitation);
    const isDirectorsPage = location.pathname.includes("directors");
    const accommodationDates = watchedValues?.Accommodation?.AccommodationDates || [];
    const getAccommodationDateRange = (selectedIds) => {
        if (invitationDates && selectedIds.length > 0) {
            const selectedDates = invitationDates
                .filter((date) => selectedIds.includes(date.id))
                .sort((a, b) => new Date(a.invitedDate).getTime() -
                new Date(b.invitedDate).getTime());
            const selectedDatesFormatted = selectedDates
                .map((date) => new Date(date.invitedDate).toLocaleDateString())
                .join(", ");
            return `Your accommodation is booked for the following dates: ${selectedDatesFormatted}.`;
        }
        return "";
    };
    const eventDateOptions = useMemo(() => {
        if (!singleEvent?.eventDates)
            return [];
        return singleEvent.eventDates.map((date) => ({
            label: formatLocalizedDate(date.date, i18n.language),
            value: date.id,
        }));
    }, [singleEvent?.eventDates, i18n.language]);
    const countOccupiedSlots = (options) => {
        let total = 0;
        for (const users of Object.values(options)) {
            total += users.length;
        }
        setCountOccupiedSpaces(total);
    };
    const getGroupedUsersByDate = useMemo(() => {
        const result = {};
        const labelCounts = {};
        (externalUsers || []).forEach((user) => {
            const fullName = `${user.firstName} ${user.middleName} ${user.lastName}`;
            if (Array.isArray(user.invitationDates)) {
                user.invitationDates.forEach((dateId) => {
                    const label = eventDateOptions.find((d) => d.value === dateId)?.label ?? dateId;
                    if (!result[label]) {
                        result[label] = [];
                        labelCounts[label] = 0;
                    }
                    result[label].push(fullName);
                    labelCounts[label] += 1;
                });
            }
        });
        // Concatenate label with invited/allowed count
        const allowedPerDateCount = 2;
        const resultWithCounts = {};
        // Sort eventDateOptions chronologically and use their labels for ordering
        eventDateOptions.forEach(({ label }) => {
            if (result[label]) {
                const invitedCount = labelCounts[label] ?? 0;
                const labelWithCount = `${label} (${invitedCount}/${externalCountData?.perDaySlotCount ?? 0} ${t("event.occupied")})`;
                resultWithCounts[labelWithCount] = result[label];
            }
        });
        countOccupiedSlots(result);
        return resultWithCounts;
    }, [externalUsers, eventDateOptions, t]);
    const calculateAge = (dob) => {
        const birthDate = new Date(dob);
        const today = new Date();
        const diffInMilliseconds = today.getTime() - birthDate.getTime();
        const ageInYears = diffInMilliseconds / (1000 * 60 * 60 * 24 * 365.25);
        return ageInYears < 1
            ? ageInYears.toFixed(1)
            : Math.floor(ageInYears).toString();
    };
    const getTransportText = (transport) => {
        const count = transport?.NoOfPeople ?? 0;
        const noOfCars = transport?.NoOfCars ?? 0;
        const hasPickupLocation = transport?.PickupLocationId;
        // Find address from pickupLocations
        const location = pickupLocations &&
            pickupLocations.find((loc) => loc.id === transport?.PickupLocationId);
        const addressText = location
            ? ` ${t("event.summaryTransportOption.location", {
                address: `${location.businessOrBoardingLocation}, ${location.postalCode} ${location.city}`,
            })}`
            : "";
        switch (Number(transport?.TransportOption)) {
            case 1: {
                const mainText = count === 1
                    ? t("event.summaryTransportOption.1", { count })
                    : t("event.summaryTransportOption.1_plural", { count });
                return mainText + "." + addressText;
            }
            case 2:
                return noOfCars === 1
                    ? t("event.summaryTransportOption.2", { noOfCars }) + "."
                    : t("event.summaryTransportOption.2_plural", { noOfCars }) + ".";
            case 3:
                return t("event.summaryTransportOption.3") + ".";
            default:
                return "";
        }
    };
    const [isScrollable, setIsScrollable] = useState(false);
    const contentRef = useRef(null);
    // Check if content is scrollable
    const checkIfScrollable = () => {
        if (contentRef.current) {
            const isScrollable = contentRef.current.scrollHeight > contentRef.current.clientHeight;
            setIsScrollable(isScrollable);
        }
    };
    useEffect(() => {
        checkIfScrollable();
    }, [watchedValues]);
    const scrollToBottom = () => {
        if (contentRef.current) {
            contentRef.current.scrollTo({
                top: contentRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    };
    return (_jsxs(Box
    // pt={{ xs: 3, lg: 0 }}
    // px={isDrawer ? 2 : { xs: 0, lg: 5 }}
    , { 
        // pt={{ xs: 3, lg: 0 }}
        // px={isDrawer ? 2 : { xs: 0, lg: 5 }}
        position: "relative", height: "100%", width: "100%", maxWidth: isBelow390 ? "auto" : isBelow1024 ? 285 : 387, mx: isBelow390 ? "auto" : isBelow1024 ? "auto" : "start", sx: {
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
        }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 5,
                    pl: isOnly1024 ? 3 : 0,
                }, children: [_jsx(Box, { width: "100%", sx: {
                            display: "flex",
                            justifyContent: isBelow390 ? "center" : "start",
                        }, children: !isDrawer && (_jsx(Box, { component: "img", src: eventType && eventType === "family"
                                ? "/assets/images/swd.png"
                                : "/assets/images/relations-day-logo.svg", sx: {
                                width: "auto",
                                height: `min(${isBelow390 ? 180 : isBelow1024 ? 160 : 260}px, 26.5dvh)`,
                            }, alt: "feature image" })) }), _jsx(Box, { sx: {
                            position: "relative",
                        }, children: _jsx(Box, { ref: contentRef, sx: {
                                msOverflowStyle: "none",
                                pb: 2,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }, children: Object.keys(watchedValues).length > 0 && (_jsxs(_Fragment, { children: [_jsx(EText, { variant: isBelow390 ? "subtitle1" : isBelow1024 ? "h6" : "h5", color: "#FFFFFF", fontWeight: 500, children: _jsx(DisplayFormattedMessage, { text: t("event.availableSpots", {
                                                occupied: t("event.invitees"),
                                                companyName: linkVerification?.data?.user?.companyName ?? "",
                                                occupiedSpaces: externalCountData?.usedSlot || 0,
                                                totalAvailableSpaces: externalCountData?.perCompanySlotCount || 0,
                                            }) }) }), _jsx(Box, { sx: {
                                            display: "flex",
                                            flexDirection: "column",
                                        }, children: Object.entries(getGroupedUsersByDate).map(([dateLabel, users], index) => (_jsxs(Box, { children: [_jsx(Divider, { sx: {
                                                        my: 1.5,
                                                        "&::before, &::after": {
                                                            borderColor: "#FFFFFF1A",
                                                        },
                                                    } }), _jsx(Typography, { variant: "subtitle1", color: "#FFFFFF", children: dateLabel }), users.map((name, idx) => (_jsx(Box, { sx: {
                                                        mt: 1,
                                                    }, children: _jsxs(Box, { sx: {
                                                            display: "flex",
                                                            gap: 1,
                                                            mt: 0.5,
                                                            alignItems: "center",
                                                        }, children: [_jsx(Chip, { variant: "outlined", sx: {
                                                                    color: theme.palette.common.white,
                                                                    height: 20,
                                                                    minWidth: 24,
                                                                    span: {
                                                                        padding: 0,
                                                                    },
                                                                }, label: `${idx + 1}` }), _jsx(Typography, { variant: "body2", color: alpha(theme.palette.common.white, 0.4), children: name })] }) }, idx)))] }, `grouped-date-${index}`))) })] })) }) })] }), isSmallScreen && (_jsx(Box, { sx: {
                    paddingTop: 6,
                }, children: _jsx(HeaderLinks, {}) }))] }));
};
